import { GameState, PieceType, MoveType } from "../../../../types";
import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "../../interfaces/DecisionPriority";
import * as Utils from "../../../utils/MCTSUtils";
import { MCTSMoveGenerationService } from "../../../utils/MCTSMoveGenerationService";
import { MonkeyHandler } from "../03_abstract-priorities/Handlers/Monkeyhandler";
import { FungiHandler } from "../03_abstract-priorities/Handlers/Fungihandler";

/**
 * Priority 2: Kingslayer Victory
 * CRITICAL FIX: Detects both capture AND trample moves that kill enemy kings
 * This priority simulates all possible ply 1 moves of the AI and if ANY of them can capture the enemy king, it must early exit and take this move!
 * Special Monkey case - IMPLEMENTED: Uses MonkeyHandler to evaluate monkey vs bear king captures based on material status and threat analysis
 * Special Fungi case - IMPLEMENTED: Uses FungiHandler to simulate zombification scenarios and prevent captures that lead to zombie race/kingslayer victories
 */
export class KingslayerVictoryPriority extends BaseDecisionPriority {
  readonly name = "Kingslayer Victory";
  readonly priority = 2;
  readonly description = "Capture enemy king for instant win";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    return true; // Always check for king kills
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    // CRITICAL FIX: Generate fresh moves to bypass any potential filtering
    // Victory priorities must ALWAYS analyze the full move set!
    const moveService = MCTSMoveGenerationService.getInstance();
    const originalMoves = moveService.getAllMoves(context.gameState);

    const kingKillMoves = this.findKingKillMoves(context, originalMoves);

    if (kingKillMoves.length === 0) {
      return {
        shouldEarlyExit: false,
        reasoning: "No enemy king kills available",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // INSTANT WIN! Take the first king kill
    const selectedMove = kingKillMoves[0];
    const piece = Utils.getPieceFromPosition(
      context.gameState,
      selectedMove.fromPosition
    );
    const targetPiece = Utils.getPieceFromPosition(
      context.gameState,
      selectedMove.move.position
    );

    this.addLog(
      `⚔️ KING KILL: ${piece?.type} ${this.formatPosition(
        selectedMove.fromPosition
      )} → ${this.formatPosition(selectedMove.move.position)} KILLS ${
        targetPiece?.type
      } KING!`
    );

    return this.createEarlyExitResult(
      selectedMove,
      `INSTANT WIN - King Kill!`,
      { logs: this.analysisLogs, totalKills: kingKillMoves.length }
    );
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  private findKingKillMoves(
    context: DecisionContext,
    movesToAnalyze: MoveInfo[]
  ): MoveInfo[] {
    const { gameState } = context;
    const enemyIsLight = !gameState.isLightTurn; // Enemy is opposite color
    const kingKills: MoveInfo[] = [];

    for (const moveInfo of movesToAnalyze) {
      // Check if move is capture OR trample (both kill pieces)
      const isKillMove =
        moveInfo.move.moveType === MoveType.capture ||
        moveInfo.move.moveType === MoveType.trample;

      if (!isKillMove) continue;

      // Check if target square has enemy king
      const targetPiece = Utils.getPieceFromPosition(
        gameState,
        moveInfo.move.position
      );
      if (
        !targetPiece ||
        !targetPiece.isKing ||
        targetPiece.isLight !== enemyIsLight
      )
        continue;

      // Get the piece making the move
      const piece = Utils.getPieceFromPosition(
        gameState,
        moveInfo.fromPosition
      );

      // SPECIAL MONKEY CASE: Check monkey vs bear king scenarios
      if (
        piece?.type === PieceType.Monkey &&
        targetPiece.type === PieceType.Bear
      ) {
        const monkeyHandler = MonkeyHandler.getInstance();
        const decision = monkeyHandler.evaluateMonkeyMove(
          moveInfo,
          gameState,
          piece.isKing
        );

        if (!decision.allowBearCapture) {
          this.addLog(
            `🚫 MONKEY BEAR CAPTURE BLOCKED: ${
              piece.isKing ? "King" : "Regular"
            } monkey vs bear king - ${decision.reasoning}`
          );
          continue; // Skip this move
        } else {
          this.addLog(
            `⚠️ RISKY MONKEY BEAR CAPTURE: ${
              piece.isKing ? "King" : "Regular"
            } monkey vs bear king - ${decision.reasoning}`
          );
        }
      }

      // SPECIAL FUNGI CASE: Check for zombification threats when capturing Fungi king
      if (targetPiece.type === PieceType.Fungi) {
        const fungiHandler = FungiHandler.getInstance();
        const decision = fungiHandler.evaluateFungiKingCapture(
          moveInfo,
          gameState
        );

        if (!decision.allowFungiKingCapture) {
          this.addLog(`🧟 FUNGI KING CAPTURE BLOCKED: ${decision.reasoning}`);
          continue; // Skip this move
        } else if (decision.canZombieRace || decision.canZombieKingslayer) {
          this.addLog(`⚠️ RISKY FUNGI KING CAPTURE: ${decision.reasoning}`);
        } else {
          this.addLog(`✅ SAFE FUNGI KING CAPTURE: ${decision.reasoning}`);
        }
      }

      kingKills.push(moveInfo);
      this.addLog(
        `🎯 Found ${moveInfo.move.moveType} kill on enemy ${
          targetPiece.type
        } king at ${this.formatPosition(moveInfo.move.position)}`
      );
    }

    return kingKills;
  }
}

